/**
 * Coordinate transformation utilities for container-relative drawing system
 * Handles conversion between global stage coordinates and container-relative coordinates
 * Supports rotation, scaling, and translation transformations
 */

/**
 * Convert global stage coordinates to container-relative coordinates
 * @param {number} globalX - Global X coordinate
 * @param {number} globalY - Global Y coordinate
 * @param {Object} container - Container object with transform properties
 * @returns {Object} - { x, y } in container-relative coordinates
 */
export const toRelativeCoords = (globalX, globalY, container) => {
  // Handle missing container
  if (!container) {
    return { x: globalX, y: globalY };
  }
  
  const {
    x: containerX = 0,
    y: containerY = 0,
    scaleX = 1,
    scaleY = 1,
    rotation = 0
  } = container;
  
  // Convert rotation to radians
  const rotationRad = (-rotation * Math.PI) / 180;
  const cos = Math.cos(rotationRad);
  const sin = Math.sin(rotationRad);
  
  // Translate to container origin
  const localX = globalX - containerX;
  const localY = globalY - containerY;
  
  // Apply inverse rotation
  const rotatedX = localX * cos - localY * sin;
  const rotatedY = localX * sin + localY * cos;
  
  // Apply inverse scaling
  return {
    x: rotatedX / scaleX,
    y: rotatedY / scaleY
  };
};

/**
 * Convert container-relative coordinates to global stage coordinates
 * @param {number} relativeX - Container-relative X coordinate
 * @param {number} relativeY - Container-relative Y coordinate
 * @param {Object} container - Container object with transform properties
 * @returns {Object} - { x, y } in global stage coordinates
 */
export const toGlobalCoords = (relativeX, relativeY, container) => {
  // Handle missing container
  if (!container) {
    return { x: relativeX, y: relativeY };
  }
  
  const {
    x: containerX = 0,
    y: containerY = 0,
    scaleX = 1,
    scaleY = 1,
    rotation = 0
  } = container;
  
  // Convert rotation to radians
  const rotationRad = (rotation * Math.PI) / 180;
  const cos = Math.cos(rotationRad);
  const sin = Math.sin(rotationRad);
  
  // Apply scaling
  const scaledX = relativeX * scaleX;
  const scaledY = relativeY * scaleY;
  
  // Apply rotation
  const rotatedX = scaledX * cos - scaledY * sin;
  const rotatedY = scaledX * sin + scaledY * cos;
  
  // Translate to global coordinates
  return {
    x: containerX + rotatedX,
    y: containerY + rotatedY
  };
};

/**
 * Convert array of points from global to container-relative coordinates
 * @param {number[]} points - Array of [x1, y1, x2, y2, ...] coordinates
 * @param {Object} container - Container object with transform properties
 * @returns {number[]} - Array of relative coordinates
 */
export const convertPointsToRelative = (points, container) => {
  if (!points || points.length === 0) return [];
  
  const relativePoints = [];
  for (let i = 0; i < points.length; i += 2) {
    const relative = toRelativeCoords(points[i], points[i + 1], container);
    relativePoints.push(relative.x, relative.y);
  }
  return relativePoints;
};

/**
 * Convert array of points from container-relative to global coordinates
 * @param {number[]} points - Array of [x1, y1, x2, y2, ...] relative coordinates
 * @param {Object} container - Container object with transform properties
 * @returns {number[]} - Array of global coordinates
 */
export const convertPointsToGlobal = (points, container) => {
  if (!points || points.length === 0) return [];
  
  const globalPoints = [];
  for (let i = 0; i < points.length; i += 2) {
    const global = toGlobalCoords(points[i], points[i + 1], container);
    globalPoints.push(global.x, global.y);
  }
  return globalPoints;
};

/**
 * Transform a drawing object from one container's coordinate system to another
 * @param {Object} drawing - Drawing object with points array
 * @param {Object} fromContainer - Source container
 * @param {Object} toContainer - Target container
 * @returns {Object} - Transformed drawing object
 */
export const transformDrawingBetweenContainers = (drawing, fromContainer, toContainer) => {
  if (!drawing || !drawing.points) return drawing;
  
  // Convert to global coordinates first
  const globalPoints = convertPointsToGlobal(drawing.points, fromContainer);
  
  // Then convert to target container coordinates
  const relativePoints = convertPointsToRelative(globalPoints, toContainer);
  
  return {
    ...drawing,
    points: relativePoints
  };
};

/**
 * Calculate the bounding box of a container in global coordinates
 * @param {Object} container - Container object
 * @returns {Object} - { left, top, right, bottom, width, height }
 */
export const getContainerBounds = (container) => {
  if (!container || !container.image) {
    return { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
  }
  
  const {
    x = 0,
    y = 0,
    scaleX = 1,
    scaleY = 1,
    image: { width = 0, height = 0 }
  } = container;
  
  const scaledWidth = width * scaleX;
  const scaledHeight = height * scaleY;
  
  return {
    left: x,
    top: y,
    right: x + scaledWidth,
    bottom: y + scaledHeight,
    width: scaledWidth,
    height: scaledHeight
  };
};

/**
 * Check if a point is inside a container's bounds
 * @param {number} x - Global X coordinate
 * @param {number} y - Global Y coordinate
 * @param {Object} container - Container object
 * @returns {boolean} - True if point is inside container
 */
export const isPointInContainer = (x, y, container) => {
  const bounds = getContainerBounds(container);
  return x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom;
};

/**
 * Find the topmost container at a given point
 * @param {number} x - Global X coordinate
 * @param {number} y - Global Y coordinate
 * @param {Object[]} containers - Array of container objects
 * @returns {Object|null} - Container at point or null
 */
export const getContainerAtPoint = (x, y, containers) => {
  if (!containers || containers.length === 0) return null;
  
  // Sort by zIndex (highest first) and check from top to bottom
  const sortedContainers = [...containers]
    .filter(container => container.visible !== false)
    .sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0));
  
  for (const container of sortedContainers) {
    if (isPointInContainer(x, y, container)) {
      return container;
    }
  }
  
  return null;
};

/**
 * Calculate the distance between two points
 * @param {number} x1 - First point X
 * @param {number} y1 - First point Y
 * @param {number} x2 - Second point X
 * @param {number} y2 - Second point Y
 * @returns {number} - Distance between points
 */
export const distance = (x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Snap a point to a grid
 * @param {number} x - X coordinate
 * @param {number} y - Y coordinate
 * @param {number} gridSize - Grid size for snapping
 * @returns {Object} - { x, y } snapped coordinates
 */
export const snapToGrid = (x, y, gridSize = 10) => {
  return {
    x: Math.round(x / gridSize) * gridSize,
    y: Math.round(y / gridSize) * gridSize
  };
};

/**
 * Calculate the center point of a container
 * @param {Object} container - Container object
 * @returns {Object} - { x, y } center coordinates in global space
 */
export const getContainerCenter = (container) => {
  if (!container || !container.image) {
    return { x: 0, y: 0 };
  }
  
  const {
    x = 0,
    y = 0,
    scaleX = 1,
    scaleY = 1,
    image: { width = 0, height = 0 }
  } = container;
  
  return {
    x: x + (width * scaleX) / 2,
    y: y + (height * scaleY) / 2
  };
};

/**
 * Check if two containers overlap
 * @param {Object} container1 - First container
 * @param {Object} container2 - Second container
 * @returns {boolean} - True if containers overlap
 */
export const containersOverlap = (container1, container2) => {
  const bounds1 = getContainerBounds(container1);
  const bounds2 = getContainerBounds(container2);
  
  return !(
    bounds1.right < bounds2.left ||
    bounds1.left > bounds2.right ||
    bounds1.bottom < bounds2.top ||
    bounds1.top > bounds2.bottom
  );
};

/**
 * Clamp a value between min and max
 * @param {number} value - Value to clamp
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {number} - Clamped value
 */
export const clamp = (value, min, max) => {
  return Math.min(Math.max(value, min), max);
};

/**
 * Linear interpolation between two values
 * @param {number} start - Start value
 * @param {number} end - End value
 * @param {number} t - Interpolation factor (0-1)
 * @returns {number} - Interpolated value
 */
export const lerp = (start, end, t) => {
  return start + (end - start) * clamp(t, 0, 1);
};

const coordinateUtils = {
  toRelativeCoords,
  toGlobalCoords,
  convertPointsToRelative,
  convertPointsToGlobal,
  transformDrawingBetweenContainers,
  getContainerBounds,
  isPointInContainer,
  getContainerAtPoint,
  distance,
  snapToGrid,
  getContainerCenter,
  containersOverlap,
  clamp,
  lerp
}; 

export default coordinateUtils; 